import Information16 from '@carbon/icons-react/es/information/16';
import Warning16 from '@carbon/icons-react/es/warning/16';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User, UserType } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import {
  AddToProjectRecordType,
  ReplacementUserForTeam,
} from 'components/organisms/resource-management/types';
import { Caption01, SingleLineEllipsis } from 'components/styles';
import { fullName } from 'utils/full-name';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { sortByText } from 'utils/sort-by-text';
import { numberSorter } from 'utils/sorters';
import { ignoreMinutesIfZero } from '../../utils/dates';
import { PartnerCompanyInfo, NameCellWithoutPadding } from './common';

const TextWithEllipsis = styled.span`
  ${SingleLineEllipsis};
`;

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
`;

const GreenText = styled.span`
  color: var(--green-50);
`;
const RedText = styled.span`
  color: var(--red-50);
`;
const SecondaryText = styled.span`
  ${Caption01};
  color: var(--gray-50);
`;
const AvailableHoursCell = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

const StyledWarning = styled(Warning16)`
  margin: 0 var(--spacing-03);
  color: var(--orange-40);
`;

const StyledInfo = styled(Information16)`
  margin: 0 var(--spacing-03);
  color: var(--gray-50);
`;

type SortReturnType = 0 | 1 | -1;

const replacementUserListSorter = (
  { entity: a }: AddToProjectRecordType<ReplacementUserForTeam>,
  { entity: b }: AddToProjectRecordType<ReplacementUserForTeam>,
): SortReturnType => sortByText(fullName(a), fullName(b));

const availabilitySorter = (
  {
    entity: { fullfilledMinutes: a },
  }: AddToProjectRecordType<ReplacementUserForTeam>,
  {
    entity: { fullfilledMinutes: b },
  }: AddToProjectRecordType<ReplacementUserForTeam>,
): number => numberSorter(a, b);

const getUserNameWithProjectStatus = (user: User, isInProject: boolean) => (
  <StyledDiv>
    <TextWithEllipsis>{fullName(user)}</TextWithEllipsis>
    {isInProject ? null : (
      <Tooltip title={<FormattedMessage id="not_in_project" />}>
        <StyledWarning />
      </Tooltip>
    )}
  </StyledDiv>
);

const renderReplacementUser = (user: ReplacementUserForTeam): JSX.Element => (
  <NameCellWithoutPadding
    primaryText={getUserNameWithProjectStatus(user, user.isInProject)}
    secondaryText={user.role?.roleName}
    logo={
      <Avatar
        src={getThumbnailUrl(user.photoUrl)}
        name={fullName(user)}
        companyLogoUrl={user.company.companyLogoUrl}
      />
    }
  />
);

const renderAvailability = (
  fullfilledMinutes,
  requiredMinutes,
): JSX.Element => {
  const stat = ignoreMinutesIfZero(fullfilledMinutes);

  return (
    <AvailableHoursCell>
      {fullfilledMinutes === requiredMinutes ? (
        <GreenText>{stat}</GreenText>
      ) : (
        <RedText>{stat}</RedText>
      )}
      <SecondaryText>
        (
        {fullfilledMinutes <= 0
          ? 0
          : Math.round((fullfilledMinutes / requiredMinutes) * 100)}
        %)
      </SecondaryText>
    </AvailableHoursCell>
  );
};

export const columnsForFindMember = (
  teamName: string,
  requiredMinutes: number,
): ColumnsType<AddToProjectRecordType<ReplacementUserForTeam>> => [
  {
    title: <FormattedMessage id="team_members" />,
    dataIndex: 'entity',
    key: 'entity',
    sortDirections: ['ascend', 'descend', 'ascend'],
    ...(requiredMinutes === 0 ? { defaultSortOrder: 'ascend' } : {}),
    width: '50%',
    sorter: replacementUserListSorter,
    render: renderReplacementUser,
  },
  {
    ...(requiredMinutes !== 0
      ? {
          title: (
            <StyledDiv>
              <FormattedMessage id="resource_management.find_member.availability" />
              <Tooltip
                title={
                  <FormattedMessage
                    id="resource_management.find_member.availability.tooltip"
                    values={{
                      hours: (
                        <strong>{ignoreMinutesIfZero(requiredMinutes)}</strong>
                      ),
                      teamName: <strong>{teamName}</strong>,
                    }}
                  />
                }
              >
                <StyledInfo />
              </Tooltip>
            </StyledDiv>
          ),
          dataIndex: 'availability',
          key: 'availability',
          sortDirections: ['ascend', 'descend', 'ascend'],
          defaultSortOrder: 'descend',
          sorter: availabilitySorter,
          render: (
            _,
            record: AddToProjectRecordType<ReplacementUserForTeam>,
          ): JSX.Element =>
            renderAvailability(
              record.entity.fullfilledMinutes,
              requiredMinutes,
            ),
        }
      : {}),
  },
  {
    title: '',
    dataIndex: 'company',
    key: 'company',
    width: 48,
    render: (_, { entity }) =>
      entity.userType === UserType.Partner && entity.company != null ? (
        <PartnerCompanyInfo companyName={entity.company.companyName} />
      ) : (
        <></>
      ),
  },
];
